/** Store → customer detail: profile, addresses, note/tags, orders. */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, Col, Descriptions, Empty, Input, Modal, Result, Row, Space, Spin, Switch, Table, Tag, Typography, message } from 'antd';
import TagsInput from '../../../shared/components/TagsInput';
import { ArrowLeftOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { StatusTag, fmtDate, listOf, money, sitePath, toastErr, useSite } from './common';
import { Address } from './OrderDetail';
import { orderName } from './Orders';

const { Text } = Typography;

const CustomerDetail = () => {
    const { t } = useTranslation();
    const { cid } = useParams();
    const navigate = useNavigate();
    const { api, siteId, currency, shop } = useSite();
    const [c, setC] = useState(null);
    const [err, setErr] = useState(null);

    const [note, setNote] = useState('');
    const [tags, setTags] = useState([]);
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [eraseOpen, setEraseOpen] = useState(false);
    const [confirmEmail, setConfirmEmail] = useState('');
    const [erasing, setErasing] = useState(false);
    const [openOrders, setOpenOrders] = useState(false);
    const [force, setForce] = useState(false);
    const exportData = async () => {
        setExporting(true);
        try { await api.download(`/customers/${cid}/export`, `customer-${cid}.zip`); } catch (e) { toastErr(e); } finally { setExporting(false); }
    };
    const erase = async () => {
        setErasing(true);
        try {
            await api.post(`/customers/${cid}/erase`, { confirm: confirmEmail, force });
            message.success(t('Customer deleted'));
            navigate(sitePath(siteId, 'customers'));
        } catch (e) {
            if (e.body?.code === 'open_orders') setOpenOrders(true);
            toastErr(e);
        } finally { setErasing(false); }
    };
    const apply = (d) => { const x = d?.customer ? { ...d.customer, orders: d.orders ?? d.customer.orders } : d; setC(x); setNote(x?.note || ''); setTags(listOf(x?.tags)); };
    useEffect(() => {
        let live = true;
        api.get(`/customers/${cid}`).then((d) => live && apply(d)).catch((e) => live && setErr(e));
        return () => { live = false; };
    }, [api, cid]);
    const update = async (patch) => {
        setSaving(true);
        try { apply(await api.put(`/customers/${cid}`, patch)); message.success(t('Saved')); } catch (e) { toastErr(e); } finally { setSaving(false); }
    };

    if (err) return <Result status={err.status === 404 ? '404' : 'error'} title={err.status === 404 ? t('Customer not found') : (err.error || t('Failed to load'))} extra={<Button onClick={() => navigate(sitePath(siteId, 'customers'))}>{t('Customers')}</Button>} />;
    if (!c) return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;
    const orders = listOf(c.orders);
    const addresses = listOf(c.addresses);

    return (
        <div>
            <Space style={{ marginBottom: 12 }}>
                <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(sitePath(siteId, 'customers'))} />
                <Text strong style={{ fontSize: 18 }}>{c.name || c.email}</Text>
                {c.state === 'disabled' && <Tag>{t('Disabled')}</Tag>}
            </Space>
            <Row gutter={16}>
                <Col xs={24} lg={16}>
                    <Card style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={8}><Text type="secondary">{t('Amount spent')}</Text><div style={{ fontSize: 20 }}>{money(c.total_spent || 0, currency)}</div></Col>
                            <Col span={8}><Text type="secondary">{t('Orders')}</Text><div style={{ fontSize: 20 }}>{c.orders_count ?? orders.length}</div></Col>
                            <Col span={8}><Text type="secondary">{t('Customer since')}</Text><div style={{ fontSize: 14, marginTop: 6 }}>{fmtDate(c.created_at, false)}</div></Col>
                        </Row>
                    </Card>
                    <Card title={t('Orders')} style={{ marginBottom: 16 }} styles={{ body: { padding: 0 } }}>
                        <Table size="small" rowKey="id" dataSource={orders} pagination={false} scroll={{ x: 560 }}
                            onRow={(o) => ({ onClick: () => navigate(sitePath(siteId, `orders/${o.id}`)), style: { cursor: 'pointer' } })}
                            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No orders yet')} /> }}
                            columns={[
                                { title: t('Order', { context: 'shop' }), key: 'n', render: (_, o) => <a>{orderName(o, shop)}</a> },
                                { title: t('Date'), dataIndex: 'created_at', render: (v) => fmtDate(v) },
                                { title: t('Payment'), dataIndex: 'financial_status', render: (v) => <StatusTag value={v} /> },
                                { title: t('Fulfillment'), dataIndex: 'fulfillment_status', render: (v) => <StatusTag value={v} /> },
                                { title: t('Total', { context: 'shop' }), key: 't', align: 'right', render: (_, o) => money(o.total, o.currency || currency) },
                            ]} />
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Card title={t('Contact')} size="small" style={{ marginBottom: 16 }}>
                        <Descriptions column={1} size="small">
                            <Descriptions.Item label={t('Email', { context: 'shop' })}><a href={`mailto:${c.email}`}>{c.email}</a></Descriptions.Item>
                            {c.phone && <Descriptions.Item label={t('Phone')}>{c.phone}</Descriptions.Item>}
                            <Descriptions.Item label={t('Marketing')}>{c.accepts_marketing ? t('Subscribed') : t('Not subscribed')}</Descriptions.Item>
                            {c.lang && <Descriptions.Item label={t('Language')}>{c.lang}</Descriptions.Item>}
                            {c.last_login_at && <Descriptions.Item label={t('Last login')}>{fmtDate(c.last_login_at)}</Descriptions.Item>}
                        </Descriptions>
                    </Card>
                    <Card title={t('Addresses')} size="small" style={{ marginBottom: 16 }}>
                        {addresses.length ? addresses.map((a, i) => <div key={i} style={{ marginBottom: 10 }}><Address a={a} /></div>) : <Text type="secondary">-</Text>}
                    </Card>
                    <Card title={t('Notes')} size="small" style={{ marginBottom: 16 }}>
                        <Input.TextArea value={note} onChange={(e) => setNote(e.target.value)} autoSize={{ minRows: 3 }} placeholder={t('Only visible to staff')} style={{ marginBottom: 8 }} />
                        <TagsInput format="array" spaceSeparates={false} value={tags} onChange={setTags} placeholder={t('Tags')} />
                        {(note !== (c.note || '') || JSON.stringify(tags) !== JSON.stringify(listOf(c.tags))) && (
                            <Button size="small" type="primary" style={{ marginTop: 8 }} loading={saving} onClick={() => update({ note, tags })}>{t('Save')}</Button>
                        )}
                    </Card>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Switch checked={c.state !== 'disabled'} onChange={(v) => update({ state: v ? 'enabled' : 'disabled' })} checkedChildren={t('Account enabled')} unCheckedChildren={t('Account disabled')} />
                        <Button block icon={<DownloadOutlined />} loading={exporting} onClick={exportData}>{t('Export data')}</Button>
                        <Button block danger icon={<DeleteOutlined />} onClick={() => { setConfirmEmail(''); setForce(false); setOpenOrders(false); setEraseOpen(true); }}>{t('Delete / anonymize')}</Button>
                    </Space>
                </Col>
            </Row>
            <Modal open={eraseOpen} title={t('Delete / anonymize')} onCancel={() => setEraseOpen(false)} onOk={erase} confirmLoading={erasing} destroyOnHidden
                okText={t('Delete')} okButtonProps={{ danger: true, disabled: confirmEmail.trim().toLowerCase() !== String(c.email).toLowerCase() || (openOrders && !force) }}>
                <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t('The account is deleted and orders stay without personal data. This cannot be undone.')} />
                <Input value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder={c.email} />
                {openOrders && <Checkbox checked={force} onChange={(e) => setForce(e.target.checked)} style={{ marginTop: 12 }}>{t('Anonymize open orders too')}</Checkbox>}
            </Modal>
        </div>
    );
};

export default CustomerDetail;
